import { cssEase, GAME_DURATION, GAME_EASE, toMs } from "@/lib/motion"
import { useEffect, useRef } from "react"
import {
  FACE_RADIUS,
  HUB_RADIUS,
  PEG_RADIUS,
  WHEEL_BOX,
  WHEEL_CENTER,
  wheelLabel,
  wheelPoint,
  wheelSlicePath,
} from "./prize-wheel-geometry"
import { WHEEL_PALETTE, type WheelToneKind } from "./prize-wheel-palette"

const VIEW_BOX = `0 0 ${WHEEL_BOX} ${WHEEL_BOX}`
const TONE_KINDS: readonly WheelToneKind[] = ["ink", "ivory", "gold", "stone"]
const { tones, gold, ink, shade } = WHEEL_PALETTE

/** One painted slice: its id, label and tone. */
export interface WheelSlice {
  id: string
  label: string
  kind: WheelToneKind
}

/** The slice the wheel stopped on and how to mark it: a pulsing gold glow for a prize, a soft outline for "Não foi dessa vez". */
export interface WheelLanding {
  index: number
  won: boolean
  animate: boolean
}

function Highlight({
  index,
  count,
  won,
  animate,
  text,
  filterId,
}: WheelLanding & { count: number; text: string; filterId: string }) {
  const ref = useRef<SVGGElement>(null)
  useEffect(() => {
    if (!won || !animate) return
    const pulse = ref.current?.animate([{ opacity: 0.35 }, { opacity: 1 }], {
      duration: toMs(GAME_DURATION.pulse),
      iterations: 5,
      direction: "alternate",
      easing: cssEase(GAME_EASE.pulse),
    })
    return () => pulse?.cancel()
  }, [won, animate])
  const path = wheelSlicePath(index, count, FACE_RADIUS - 2)
  if (!won) return <path d={path} fill="none" stroke={text} strokeOpacity={0.45} strokeWidth={2.5} strokeLinejoin="round" />
  return (
    <g ref={ref} filter={`url(#${filterId})`}>
      <path d={path} fill="none" stroke={ink} strokeOpacity={0.6} strokeWidth={6.5} strokeLinejoin="round" />
      <path d={path} fill="none" stroke={gold.mid} strokeWidth={4} strokeLinejoin="round" />
      <path d={path} fill="none" stroke={gold.light} strokeWidth={1.4} strokeLinejoin="round" />
    </g>
  )
}

/** The turning face: ink, ivory and stone slices with a hub-to-rim gradient, gold separators and pegs, fitted labels, and the landed slice marked (gold glow on a prize, soft outline on a miss). */
export function WheelFace({
  uid,
  slices,
  landing,
}: {
  uid: string
  slices: readonly WheelSlice[]
  landing: WheelLanding | null
}) {
  const count = slices.length
  const step = 360 / Math.max(1, count)
  return (
    <svg viewBox={VIEW_BOX} className="h-full w-full">
      <defs>
        {TONE_KINDS.map((kind) => (
          <radialGradient
            key={kind}
            id={`${uid}-${kind}`}
            gradientUnits="userSpaceOnUse"
            cx={WHEEL_CENTER}
            cy={WHEEL_CENTER}
            r={FACE_RADIUS}
          >
            <stop offset="0.25" stopColor={tones[kind].inner} />
            <stop offset="1" stopColor={tones[kind].outer} />
          </radialGradient>
        ))}
        <radialGradient id={`${uid}-peg`} cx="0.35" cy="0.35" r="0.75">
          <stop offset="0" stopColor={gold.light} />
          <stop offset="1" stopColor={gold.deep} />
        </radialGradient>
        {landing ? (
          <filter id={`${uid}-landing`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        ) : null}
      </defs>
      {slices.map((slice, index) => (
        <path key={slice.id} d={wheelSlicePath(index, count)} fill={`url(#${uid}-${slice.kind})`} />
      ))}
      {count > 1
        ? slices.map((slice, index) => {
            const from = wheelPoint(index * step, HUB_RADIUS)
            const to = wheelPoint(index * step, FACE_RADIUS)
            return (
              <g key={`separator-${slice.id}`}>
                <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={shade} strokeOpacity={0.2} strokeWidth={3.2} />
                <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={gold.mid} strokeWidth={1.4} />
              </g>
            )
          })
        : null}
      {slices.map((slice, index) => {
        const label = wheelLabel(slice.label, index, count)
        const firstY = WHEEL_CENTER - ((label.lines.length - 1) * label.lineHeight) / 2
        return (
          <text
            key={`label-${slice.id}`}
            transform={`rotate(${label.rotation} ${WHEEL_CENTER} ${WHEEL_CENTER})`}
            textAnchor="middle"
            dominantBaseline="central"
            fill={tones[slice.kind].text}
            className="font-game-display font-bold"
            style={{ fontSize: label.fontSize }}
          >
            {label.lines.map((line, row) => (
              <tspan key={`${row}:${line}`} x={label.x} y={firstY + row * label.lineHeight}>
                {line}
              </tspan>
            ))}
          </text>
        )
      })}
      {count > 1
        ? slices.map((slice, index) => {
            const peg = wheelPoint(index * step, PEG_RADIUS)
            return (
              <circle
                key={`peg-${slice.id}`}
                cx={peg.x}
                cy={peg.y}
                r={2.7}
                fill={`url(#${uid}-peg)`}
                stroke={shade}
                strokeOpacity={0.3}
                strokeWidth={0.6}
              />
            )
          })
        : null}
      {landing ? (
        <Highlight
          key={`${landing.index}:${landing.won}`}
          {...landing}
          count={count}
          filterId={`${uid}-landing`}
          text={tones[slices[landing.index]?.kind ?? "stone"].text}
        />
      ) : null}
    </svg>
  )
}
